import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

export type SystemHint = { id: string; label: string; reason: string }
export type RecommendedAction = { id: string; message: string }

export type InformationSystemSummary = {
  id: string
  organizationId: string
  name: string
  code: string | null
  description: string | null
  businessOwner: string | null
  technicalOwner: string | null
  businessOwnerUserId?: string | null
  technicalOwnerUserId?: string | null
  businessOwnerDisplay?: string | null
  technicalOwnerDisplay?: string | null
  environment: string | null
  criticality: string | null
  lifecycleStatus: string | null
  dataCategoriesCount: number
  linkedAssetsCount: number
  riskScore: number | null
  complianceScore: number | null
  hints?: SystemHint[]
  recommendedActions?: RecommendedAction[]
  createdAt?: string
  updatedAt?: string
}

export type InformationSystemDetail = InformationSystemSummary & {
  assetLinks?: Array<{
    id: string
    assetId: string | number
    relationshipType: string | null
    notes: string | null
    asset?: { id: string | number; name: string; type?: string; environment?: string; criticality?: string; riskLevel?: string }
  }>
  dataCategories?: DataCategoryAssignment[]
  riskCompliancePreview?: {
    riskScore: null
    complianceScore: null
    vulnerabilityCount: number | null
    evidenceCoverage: null
    evidenceCoverageNote?: string
  }
}

export type DataCategoryAssignment = {
  id: string
  category: string
  classification?: string | null
  processingPurpose?: string | null
  storageLocation?: string | null
  retentionPeriod?: string | null
  crossBorderTransfer: boolean
  notes?: string | null
}

export type RiskComplianceSummary = {
  informationSystemId: string
  name: string
  lifecycleStatus?: string | null
  criticality: string | null
  linkedAssetsCount: number
  dataCategoriesCount?: number
  vulnerabilityCount: number | null
  dataCategories: Array<{ id: string; category: string; classification: string | null }>
  hints?: SystemHint[]
  recommendedActions?: RecommendedAction[]
  applicableStandards: null
  evidenceCoverage: null
  evidenceCoverageNote?: string
  openFindings: null
  riskScore: null
  complianceScore: null
}

type ApiResult<T> = { success: boolean; data?: T; message?: string }

const LINKABLE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/** Fallback when catalog API is empty — matches backend PREDEFINED_DATA_CATEGORIES. */
export const DATA_CATEGORY_CATALOG_FALLBACK: string[] = [
  'Personal Data',
  'Special Category Personal Data',
  'Banking Secrecy',
  'Commercial Secret',
  'Payment Data',
  'Authentication Data',
  'Cryptographic Keys',
  'Logs / Telemetry',
  'Financial Data',
  'Customer Data',
  'Medical Data',
  'Biometric Data',
  'Employee Data',
  'Confidential Documents',
]

/** Inventory Asset rows (integer or UUID id) can be linked; scan-only ra-* ids cannot. */
export function isLinkableAssetId(id: string | number | null | undefined): boolean {
  if (id == null) return false
  if (typeof id === 'number' && Number.isInteger(id) && id > 0) return true
  const s = String(id).trim()
  if (/^ra-/i.test(s)) return false
  if (/^\d+$/.test(s)) return true
  return LINKABLE_UUID_RE.test(s)
}

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) {
    throw new Error(body?.message || `Request failed (${res.status})`)
  }
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export async function fetchInformationSystems(
  token: string,
  orgId?: string | null,
  filters?: {
    lifecycleStatus?: string
    includeArchived?: boolean
    ownerMissing?: boolean
    noAssets?: boolean
    noDataCategories?: boolean
    noStandards?: boolean
    noControls?: boolean
    noEvidence?: boolean
  }
) {
  return apiFetch<InformationSystemSummary[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS}${orgQuery(orgId, {
      lifecycleStatus: filters?.lifecycleStatus,
      includeArchived: filters?.includeArchived ? 'true' : undefined,
      ownerMissing: filters?.ownerMissing ? 'true' : undefined,
      noAssets: filters?.noAssets ? 'true' : undefined,
      noDataCategories: filters?.noDataCategories ? 'true' : undefined,
      noStandards: filters?.noStandards ? 'true' : undefined,
      noControls: filters?.noControls ? 'true' : undefined,
      noEvidence: filters?.noEvidence ? 'true' : undefined,
    })}`,
    token
  )
}

export async function fetchLifecycleCatalog(token: string) {
  return apiFetch<string[]>(API_ENDPOINTS.INFORMATION_SYSTEMS_LIFECYCLE_CATALOG, token)
}

export async function archiveInformationSystem(token: string, id: string, orgId?: string | null) {
  return apiFetch<InformationSystemSummary>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_ARCHIVE(id)}${orgQuery(orgId)}`,
    token,
    { method: 'POST' }
  )
}

export async function bulkLinkAssets(token: string, systemId: string, assetIds: Array<string | number>, orgId?: string | null) {
  return apiFetch<{ linked: Array<string | number>; skipped: Array<string | number>; errors: Array<{ assetId: string | number; message: string }> }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_ASSETS_BULK(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify({ assetIds }) }
  )
}

export async function bulkUnlinkAssets(token: string, systemId: string, assetIds: Array<string | number>, orgId?: string | null) {
  return apiFetch<{ unlinked: Array<string | number>; errors: Array<{ assetId: string | number; message: string }> }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_ASSETS_BULK_UNLINK(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify({ assetIds }) }
  )
}

export type OrgUserOption = { id: string; username: string; email: string }

export async function fetchOrgUsers(token: string) {
  const res = await fetch(API_ENDPOINTS.ORG_CURRENT_USERS, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const body = await safeJson(res)
  return (body?.users || []) as OrgUserOption[]
}

export async function fetchInformationSystem(token: string, id: string, orgId?: string | null) {
  return apiFetch<InformationSystemDetail>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM(id)}${orgQuery(orgId)}`,
    token
  )
}

export async function createInformationSystem(
  token: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<InformationSystemSummary>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function updateInformationSystem(
  token: string,
  id: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<InformationSystemSummary>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM(id)}${orgQuery(orgId)}`,
    token,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
}

export async function deleteInformationSystem(token: string, id: string, orgId?: string | null) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM(id)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function linkAssetToSystem(
  token: string,
  systemId: string,
  assetId: string,
  orgId?: string | null,
  extras?: { relationshipType?: string; notes?: string }
) {
  return apiFetch<unknown>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_ASSETS(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify({ assetId, ...extras }) }
  )
}

export async function unlinkAssetFromSystem(
  token: string,
  systemId: string,
  assetId: string,
  orgId?: string | null
) {
  return apiFetch<{ unlinked: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_ASSET(systemId, assetId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function fetchDataCategoryCatalog(token: string) {
  return apiFetch<string[]>(API_ENDPOINTS.INFORMATION_SYSTEMS_DATA_CATEGORY_CATALOG, token)
}

export async function createDataCategoryAssignment(
  token: string,
  systemId: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<DataCategoryAssignment>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_CATEGORIES(systemId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function updateDataCategoryAssignment(
  token: string,
  systemId: string,
  dataCategoryId: string,
  payload: Record<string, unknown>,
  orgId?: string | null
) {
  return apiFetch<DataCategoryAssignment>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_CATEGORY(systemId, dataCategoryId)}${orgQuery(orgId)}`,
    token,
    { method: 'PATCH', body: JSON.stringify(payload) }
  )
}

export async function deleteDataCategoryAssignment(
  token: string,
  systemId: string,
  dataCategoryId: string,
  orgId?: string | null
) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_CATEGORY(systemId, dataCategoryId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function fetchDataMap(
  token: string,
  orgId?: string | null,
  filters?: { lifecycleStatus?: string; category?: string; classification?: string }
) {
  return apiFetch<
    Array<{
      informationSystemId: string
      informationSystemName: string
      informationSystemCode: string | null
      lifecycleStatus?: string | null
      dataCategories: DataCategoryAssignment[]
    }>
  >(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_DATA_MAP}${orgQuery(orgId, {
      lifecycleStatus: filters?.lifecycleStatus,
      category: filters?.category,
      classification: filters?.classification,
    })}`,
    token
  )
}

export async function fetchRiskComplianceSummaries(
  token: string,
  systems: InformationSystemSummary[],
  orgId?: string | null
) {
  const results: RiskComplianceSummary[] = []
  for (const s of systems) {
    try {
      const row = await apiFetch<RiskComplianceSummary>(
        `${API_ENDPOINTS.INFORMATION_SYSTEM_RISK_COMPLIANCE(s.id)}${orgQuery(orgId)}`,
        token
      )
      results.push(row)
    } catch {
      results.push({
        informationSystemId: s.id,
        name: s.name,
        lifecycleStatus: s.lifecycleStatus,
        criticality: s.criticality,
        linkedAssetsCount: s.linkedAssetsCount,
        dataCategoriesCount: s.dataCategoriesCount,
        vulnerabilityCount: null,
        dataCategories: [],
        hints: s.hints || [],
        recommendedActions: s.recommendedActions || [],
        applicableStandards: null,
        evidenceCoverage: null,
        openFindings: null,
        riskScore: null,
        complianceScore: null,
      })
    }
  }
  return results
}
